import React, { useCallback, useRef, useState } from 'react';
import Button from '../../../components/Atoms/Button';
import { useI18n } from '../../../i18n';
import { ACCEPT_ATTRIBUTE } from '../utils/imageQueue';
import { formatDuration, typeKey, versionLabel } from '../utils/predictionView';
import PredictionCanvas from './PredictionCanvas';

/** `fa-*` name per queue state. Every one checked against the FA5 sheet by `icons.test.js`. */
const STATUS_ICON = {
  pending: 'fa-clock',
  running: 'fa-spinner fa-spin',
  done: 'fa-check-circle',
  failed: 'fa-exclamation-circle',
};

const PredictionPanel = ({
  selected,
  images,
  activeImage,
  activeDetections,
  rejected,
  running,
  canRun,
  conf,
  progress,
  highlighted,
  handlers,
}) => {
  const { t } = useI18n();
  const inputRef = useRef(null);
  const [dragging, setDragging] = useState(false);

  const onDrop = useCallback(
    (event) => {
      event.preventDefault();
      setDragging(false);
      // The WHOLE list, never `files[0]`. A drop of forty photos minus
      // thirty-nine is not what the user asked for — the assistant's attach
      // shipped exactly that shape and did nothing at all (2026-09-14).
      handlers.handleAttach(event.dataTransfer?.files);
    },
    [handlers],
  );

  if (!selected) {
    return (
      <div className="lf-predict-panel lf-predict-panel--center">
        <div className="lf-predict-empty">
          <i className="fas fa-hand-point-left" aria-hidden="true"></i>
          <p className="lf-predict-empty__title">{t('prediction.panel.pickModel')}</p>
          <p className="lf-predict-empty__hint">{t('prediction.panel.pickModelHint')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="lf-predict-panel lf-predict-panel--center">
      <div className="lf-predict-panel__head">
        <h2 className="lf-predict-panel__title">{versionLabel(selected)}</h2>
        <span className="lf-predict-panel__sub">{t(`prediction.type.${typeKey(selected.model_type)}`)}</span>
      </div>

      {/* The drop target is the whole BODY, not only the empty-state placeholder.
          Scoped to the placeholder it works exactly once and then silently
          stops, because the placeholder is gone as soon as one image lands. */}
      <div
        className={`lf-predict-panel__body${dragging ? ' is-dragging' : ''}`}
        onDrop={onDrop}
        onDragOver={(event) => {
          event.preventDefault();
          setDragging(true);
        }}
        onDragLeave={(event) => {
          // `dragleave` bubbles from every child, so clearing unconditionally
          // makes the highlight strobe as the cursor crosses each thumbnail.
          if (!event.currentTarget.contains(event.relatedTarget)) setDragging(false);
        }}
      >
        {!images.length ? (
          <div
            className="lf-predict-drop"
            onClick={() => inputRef.current?.click()}
            role="button"
            tabIndex={0}
            onKeyDown={(event) => {
              if (event.key === 'Enter' || event.key === ' ') inputRef.current?.click();
            }}
          >
            <i className="fas fa-images" aria-hidden="true"></i>
            <p className="lf-predict-drop__title">{t('prediction.panel.dropTitle')}</p>
            <p className="lf-predict-drop__hint">{t('prediction.panel.dropHint')}</p>
          </div>
        ) : (
          <>
            {activeImage && (
              <PredictionCanvas
                image={activeImage}
                result={activeImage.result}
                detections={activeImage.status === 'done' ? activeDetections : []}
                highlighted={highlighted}
                onHighlight={handlers.setHighlightedDetection}
              />
            )}

            {activeImage?.status === 'failed' && (
              <p className="lf-predict-inline-error">
                <i className="fas fa-exclamation-triangle" aria-hidden="true"></i> {activeImage.error}
              </p>
            )}

            <div className="lf-predict-strip" role="list">
              {images.map((item) => (
                <div
                  key={item.id}
                  role="listitem"
                  className={`lf-predict-thumb${item.id === activeImage?.id ? ' is-active' : ''}`}
                >
                  <button
                    type="button"
                    className="lf-predict-thumb__btn"
                    onClick={() => handlers.setActiveImage(item.id)}
                    title={item.name}
                  >
                    {item.previewUrl ? (
                      <img src={item.previewUrl} alt={item.name} />
                    ) : (
                      <i className="fas fa-image" aria-hidden="true"></i>
                    )}
                    <span className={`lf-predict-thumb__status is-${item.status}`}>
                      <i className={`fas ${STATUS_ICON[item.status] || 'fa-clock'}`} aria-hidden="true"></i>
                    </span>
                  </button>
                  <button
                    type="button"
                    className="lf-predict-thumb__remove"
                    onClick={() => handlers.removeImage(item.id)}
                    title={t('common.remove')}
                    disabled={running}
                  >
                    <i className="fas fa-times" aria-hidden="true"></i>
                    <span className="lf-sr-only">{t('common.remove')}</span>
                  </button>
                </div>
              ))}
              <button
                type="button"
                className="lf-predict-thumb lf-predict-thumb--add"
                onClick={() => inputRef.current?.click()}
                disabled={running}
                title={t('prediction.panel.addMore')}
              >
                <i className="fas fa-plus" aria-hidden="true"></i>
                <span className="lf-sr-only">{t('prediction.panel.addMore')}</span>
              </button>
            </div>
          </>
        )}

        {rejected.length > 0 && (
          <ul className="lf-predict-rejected">
            {rejected.map((item) => (
              <li key={item.name}>
                <i className="fas fa-ban" aria-hidden="true"></i> {t(`prediction.rejected.${item.reason}`, { name: item.name })}
              </li>
            ))}
          </ul>
        )}

        <input
          ref={inputRef}
          type="file"
          accept={ACCEPT_ATTRIBUTE}
          multiple
          className="lf-predict-file"
          onChange={(event) => {
            handlers.handleAttach(event.target.files);
            // Cleared so re-picking the SAME file fires `change` again —
            // without this, removing an image and re-adding it does nothing.
            event.target.value = '';
          }}
        />
      </div>

      <div className="lf-predict-controls">
        <label className="lf-predict-slider">
          <span className="lf-predict-slider__label">
            {t('prediction.panel.confidence')} <strong>{Math.round(conf * 100)}%</strong>
          </span>
          <input
            type="range"
            min="0"
            max="1"
            step="0.05"
            value={conf}
            onChange={(event) => handlers.setConf(Number(event.target.value))}
          />
        </label>

        <div className="lf-predict-controls__actions">
          {images.length > 0 && (
            <span className="lf-predict-progress">
              {running
                ? t('prediction.panel.progressRunning', { done: progress.settled, total: progress.total })
                : t('prediction.panel.progressIdle', { done: progress.done, total: progress.total })}
              {activeImage?.result?.inference_ms !== undefined && !running && (
                <span className="lf-predict-progress__ms">
                  <i className="fas fa-stopwatch" aria-hidden="true"></i>{' '}
                  {formatDuration(activeImage.result.inference_ms)}
                </span>
              )}
            </span>
          )}
          {running ? (
            <Button variant="danger" size="sm" onClick={handlers.handleStop}>
              <i className="fas fa-stop" aria-hidden="true"></i> {t('prediction.panel.stop')}
            </Button>
          ) : (
            <Button variant="primary" size="sm" onClick={handlers.handleRun} disabled={!canRun}>
              <i className="fas fa-bolt" aria-hidden="true"></i>{' '}
              {progress.done ? t('prediction.panel.runAgain') : t('prediction.panel.run')}
            </Button>
          )}
          {images.length > 0 && (
            <Button variant="ghost" size="sm" onClick={handlers.clearImages} disabled={running}>
              <i className="fas fa-trash-alt" aria-hidden="true"></i> {t('prediction.panel.clear')}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

export default PredictionPanel;
